import { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { KeyRound, User, Mail, Store } from "lucide-react";

export default function Settings() {
  const { user } = useAuth();
  const [form, setForm] = useState({ current_password: "", new_password: "", confirm: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.new_password !== form.confirm) {
      toast.error("New passwords don't match");
      return;
    }
    if (form.new_password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current_password,
        new_password: form.new_password,
      });
      toast.success("Password changed successfully");
      setForm({ current_password: "", new_password: "", confirm: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setLoading(false);
  };

  return (
    <div className="space-y-8 max-w-2xl" data-testid="settings-root">
      <div>
        <div className="text-xs uppercase tracking-widest text-amber-500 font-mono-plex mb-2">
          Account
        </div>
        <h1 className="font-display text-4xl font-bold">Settings</h1>
        <p className="text-slate-500 text-sm mt-1">
          Manage your account and security.
        </p>
      </div>

      <div className="surface-card rounded-xl p-6 space-y-4">
        <div className="font-display text-2xl font-bold mb-2">Profile</div>
        <div className="space-y-3 text-sm">
          <ProfileRow icon={User} label="Name" value={user?.name} />
          <ProfileRow icon={Mail} label="Email" value={user?.email} />
          <ProfileRow icon={Store} label="Bar" value={user?.bar_name} />
          <ProfileRow icon={KeyRound} label="Plan" value={user?.plan || "free"} capitalize />
        </div>
      </div>

      <form onSubmit={submit} className="surface-card rounded-xl p-6 space-y-4" data-testid="change-password-form">
        <div className="font-display text-2xl font-bold">Change password</div>
        <Field label="Current password" required>
          <input
            type="password"
            value={form.current_password}
            onChange={(e) => setForm({ ...form, current_password: e.target.value })}
            required
            data-testid="cp-current"
            className={inp}
          />
        </Field>
        <Field label="New password" required>
          <input
            type="password"
            minLength={6}
            value={form.new_password}
            onChange={(e) => setForm({ ...form, new_password: e.target.value })}
            required
            data-testid="cp-new"
            className={inp}
          />
        </Field>
        <Field label="Confirm new password" required>
          <input
            type="password"
            minLength={6}
            value={form.confirm}
            onChange={(e) => setForm({ ...form, confirm: e.target.value })}
            required
            data-testid="cp-confirm"
            className={inp}
          />
        </Field>
        <div className="pt-2">
          <button
            type="submit"
            disabled={loading}
            data-testid="cp-submit"
            className="btn-amber rounded-md px-4 py-2 text-sm disabled:opacity-60"
          >
            {loading ? "Updating…" : "Change password"}
          </button>
        </div>
      </form>
    </div>
  );
}

const inp = "w-full bg-[#0a0c10] border border-white/10 rounded-md px-3 py-2 text-white text-sm outline-none focus:border-amber-500";

const Field = ({ label, required, children }) => (
  <label className="block">
    <div className="text-xs text-slate-400 mb-1">
      {label} {required && <span className="text-amber-500">*</span>}
    </div>
    {children}
  </label>
);

const ProfileRow = ({ icon: Icon, label, value, capitalize }) => (
  <div className="flex items-center gap-3">
    <Icon className="w-4 h-4 text-slate-500" />
    <div className="text-xs text-slate-500 uppercase tracking-widest font-mono-plex w-16">
      {label}
    </div>
    <div className={`text-slate-200 ${capitalize ? "capitalize" : ""}`}>{value || "—"}</div>
  </div>
);
